import { formatAppDateTimeApi } from './dateFormats';

export type FilePathEntry = {
    label: string;
    path: string;
    /** Preferred path for its label; UE still picks by engine version, first match wins. */
    primary?: boolean;
    /** Last edit time for this row (set when label/path changes). */
    timestamp?: string;
};

const isPrimaryFlag = (value: unknown): boolean => value === true || value === 'true' || value === 1;

export const nowFilePathTimestamp = (date: Date = new Date()): string =>
    formatAppDateTimeApi(date) ?? '';

const readOptionalString = (value: unknown): string =>
    typeof value === 'string' ? value.trim() : '';

const withOptionalMeta = (
    label: string,
    path: string,
    options?: { primary?: boolean; timestamp?: string },
): FilePathEntry => {
    const entry: FilePathEntry = { label, path };
    if (options?.primary === true) {
        entry.primary = true;
    }
    const timestamp = (options?.timestamp ?? '').trim();
    if (timestamp) {
        entry.timestamp = timestamp;
    }
    return entry;
};

export const parseFilePathEntries = (value: string): FilePathEntry[] => {
    const trimmed = (value ?? '').trim();
    if (!trimmed) {
        return [];
    }

    try {
        const parsed = JSON.parse(trimmed) as unknown;
        if (Array.isArray(parsed)) {
            return parsed
                .map((item) => {
                    if (!item || typeof item !== 'object') {
                        return null;
                    }

                    const label = readOptionalString((item as { label?: unknown }).label);
                    const path = readOptionalString((item as { path?: unknown }).path);
                    const primary = isPrimaryFlag((item as { primary?: unknown }).primary);
                    const timestamp = readOptionalString((item as { timestamp?: unknown }).timestamp);

                    if (!label && !path) {
                        return null;
                    }

                    return withOptionalMeta(label, path, { primary, timestamp });
                })
                .filter((entry): entry is FilePathEntry => entry !== null);
        }
    } catch {
        // Fall through to line parser for non-JSON and legacy values.
    }

    return trimmed
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
            const match = line.match(/^(?<label>.+?)\s+(?<path>[A-Za-z]:\\.+)$/);
            if (match?.groups) {
                return {
                    label: match.groups.label.trim(),
                    path: match.groups.path.trim(),
                };
            }

            return {
                label: '',
                path: line,
            };
        });
};

export const toFilePathEditorValue = (value: string): string => {
    const entries = parseFilePathEntries(value);
    if (!entries.length) {
        return (value ?? '').trim();
    }

    return entries.map((entry) => (entry.label ? `${entry.label} ${entry.path}` : entry.path)).join('\n');
};

export const toFilePathEditorValueFromEntries = (entries: FilePathEntry[]): string => entries
    .map((entry) => {
        const label = (entry.label ?? '').trim();
        const path = (entry.path ?? '').trim();

        if (label && path) {
            return `${label} ${path}`;
        }

        return path || label;
    })
    .filter(Boolean)
    .join('\n');

/** Primary rows first (stable), then the rest — so UE version match picks preferred path. */
export const sortFilePathEntriesByPrimary = (entries: FilePathEntry[]): FilePathEntry[] => {
    const primary: FilePathEntry[] = [];
    const rest: FilePathEntry[] = [];
    for (const entry of entries) {
        if (entry.primary) {
            primary.push(entry);
        } else {
            rest.push(entry);
        }
    }
    return [...primary, ...rest];
};

export const normalizeFilePathEntries = (entries: FilePathEntry[]): FilePathEntry[] => {
    const normalized: FilePathEntry[] = [];
    for (const entry of entries) {
        const label = (entry.label ?? '').trim();
        const path = (entry.path ?? '').trim();
        if (!label && !path) {
            continue;
        }
        normalized.push(
            withOptionalMeta(label, path, {
                primary: entry.primary === true,
                timestamp: entry.timestamp,
            }),
        );
    }
    return normalized;
};

export const toFilePathPayloadValue = (value: string): string => {
    const entries = parseFilePathEntries(value);
    if (!entries.length) {
        return '';
    }

    return toFilePathPayloadFromEntries(entries);
};

/** Build API payload directly from row state (avoids form.paths desync during autosave). */
export const toFilePathPayloadFromEntries = (entries: FilePathEntry[]): string => {
    const normalized = sortFilePathEntriesByPrimary(normalizeFilePathEntries(entries));

    if (!normalized.length) {
        return '';
    }

    return JSON.stringify(normalized);
};

export const moveFilePathEntry = (entries: FilePathEntry[], fromIndex: number, toIndex: number): FilePathEntry[] => {
    if (
        fromIndex < 0
        || toIndex < 0
        || fromIndex >= entries.length
        || toIndex >= entries.length
        || fromIndex === toIndex
    ) {
        return entries;
    }

    const next = [...entries];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next;
};

/**
 * Toggle primary on a row. Multiple rows may be primary at once
 * (including the same version label).
 */
export const setPrimaryFilePathEntry = (entries: FilePathEntry[], index: number, primary = true): FilePathEntry[] => {
    if (index < 0 || index >= entries.length) {
        return entries;
    }

    return entries.map((entry, rowIndex) => {
        if (rowIndex !== index) {
            return entry;
        }

        return withOptionalMeta(entry.label, entry.path, {
            primary,
            timestamp: entry.timestamp,
        });
    });
};

/** Stamp current edit time onto a row (label/path change). */
export const touchFilePathEntry = (entry: FilePathEntry, date: Date = new Date()): FilePathEntry => {
    const timestamp = nowFilePathTimestamp(date);
    return withOptionalMeta(entry.label, entry.path, {
        primary: entry.primary === true,
        timestamp,
    });
};
